<template>
  <div class="flex-grow-1">
    <v-tabs
      v-model="selectedIndex"
      :class="`white pl-${leftPad}`"
      :height="dense ? '42px' : undefined"
    >
      <v-tab v-for="(tab, i) in computedTabs" :key="tab.label">
        <span
          class="text-none font-weight-bold"
          :class="{ 'py-3': dense }"
        >
          {{ tab.label }}
        </span>
        <v-chip
          v-if="tab.count >= 0"
          x-small
          class="px-2"
          :class="chipClasses(i === selectedIndex)"
        >
          <div class="font-weight-bold count-text">
            {{ tab.count }}
          </div>
        </v-chip>
      </v-tab>
    </v-tabs>
    <div class="fill">
      <v-tabs-items
        class="page-tab-items transparent fill-height"
        :value="selectedIndex"
      >
        <slot></slot>
      </v-tabs-items>
    </div>
  </div>
</template>

<script>
import { kebabCase } from 'lodash';

export default {
  name: 'PageTabs',
  props: {
    value: {
      type: Number,
      default: 0,
    },
    tabs: {
      type: Array,
      default: () => [],
    },
    valueProp: {
      type: String,
      default: null,
    },
    countProp: {
      type: String,
      default: null,
    },
    leftPad: {
      type: [Number, String],
      default: 9,
    },
    dense: {
      type: Boolean,
      default: false,
    },
    useTabRoutes: {
      type: Boolean,
      default: false,
    },
    tabRouteParam: {
      type: String,
      default: 'tab',
    },
  },
  emits: ['input'],
  computed: {
    selectedIndex: {
      get: function () {
        if (!this.useTabRoutes) return this.value;

        const path = this.$route.params[this.tabRouteParam];
        const index = this.paths.indexOf(path);
        return index >= 0 ? index : this.value;
      },
      set: function (value) {
        this.$emit('input', value);
        if (!this.useTabRoutes) return;

        // If the value is 0, we want to go to the root path
        // Otherwise, we want to go to the tab path
        value === 0
          ? this.toRootPath()
          : this.toTabPathByIndex(value);
      },
    },
    paths() {
      return this.tabs.map((tab) => {
        if (this.valueProp) {
          return kebabCase(tab[this.valueProp]);
        } else {
          return kebabCase(tab);
        }
      });
    },
    computedTabs() {
      return this.tabs.map((tab) => {
        const retVal = {};
        if (this.valueProp) {
          retVal.label = this.titleCase(
            tab[this.valueProp].toLowerCase()
          );
        } else {
          retVal.label = this.titleCase(tab.toLowerCase());
        }
        if (this.countProp) {
          retVal.count = tab[this.countProp];
        }
        return retVal;
      });
    },
  },
  methods: {
    chipClasses(selected) {
      return {
        ['ml-2 grey--text text--darken-2']: !selected,
        'ml-2 primary': selected,
        ['ml-2 white--text']: selected,
      };
    },
    /**
     * Converts the given string to Title Case while preserving
     * special characters (besides underscore, which is used to support
     * UPPER_SNAKE_CASE).
     */
    titleCase(str) {
      return str
        .toLowerCase()
        .split(/[\s_]/)
        .map((word) => {
          try {
            const firstLetter = word.match(/\w/)[0];
            return word.replace(
              firstLetter,
              firstLetter.toUpperCase()
            );
          } catch (e) {
            return word;
          }
        })
        .join(' ');
    },
    toRootPath() {
      const currentPath = location.pathname.split('/').pop();
      const basePath = location.pathname.replace(
        new RegExp(`/${currentPath}$`),
        ''
      );
      this.$router.push({
        path: basePath,
        query: this.$route.query,
      });
    },
    toTabPathByIndex(tabIndex) {
      if (this.$route.params[this.tabRouteParam]) {
        this.$router.push({
          params: {
            [this.tabRouteParam]: this.paths[tabIndex],
          },
        });
      } else {
        this.$router.push({
          path: `${location.pathname}/${this.paths[tabIndex]}`,
          query: this.$route.query,
        });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.fill {
  height: calc(100% - #{$page-tabs-height});
}
.count-text {
  // This is a hack to get the text to be centered in the chip
  letter-spacing: 0;
}
</style>

<style lang="scss">
.page-tab-items .v-window-item {
  height: 100%;
}
</style>
